"use client";

import { useState } from "react";
import { Moon, Cloud } from "lucide-react";
import type { SleepGoal } from "@/lib/types";

type GoalsTimelineProps = {
  goal: SleepGoal;
};

// Helper function to convert time string to minutes since midnight
// Supports both 24-hour format (e.g., "19:30") and 12-hour format (e.g., "7:30 PM")
function timeToMinutes(timeStr: string): number {
  // Try 12-hour format first (with AM/PM)
  const twelveHourMatch = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (twelveHourMatch) {
    let hours = parseInt(twelveHourMatch[1]);
    const minutes = parseInt(twelveHourMatch[2]);
    const period = twelveHourMatch[3].toUpperCase();

    if (period === 'PM' && hours !== 12) {
      hours += 12;
    } else if (period === 'AM' && hours === 12) {
      hours = 0;
    }

    return hours * 60 + minutes;
  }

  // Try 24-hour format (HH:mm)
  const twentyFourHourMatch = timeStr.match(/(\d+):(\d+)/);
  if (twentyFourHourMatch) {
    const hours = parseInt(twentyFourHourMatch[1]);
    const minutes = parseInt(twentyFourHourMatch[2]);
    return hours * 60 + minutes;
  }

  return 0;
}

// Helper to format minutes to 12-hour time string
function minutesToTimeString(minutes: number): string {
  const hours = Math.floor(minutes / 60) % 24;
  const mins = minutes % 60;
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return `${displayHours}:${mins.toString().padStart(2, '0')} ${period}`;
}

// Helper to format duration
function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (hours > 0 && mins > 0) {
    return `${hours}h ${mins}m`;
  } else if (hours > 0) {
    return `${hours}h`;
  } else {
    return `${mins}m`;
  }
}

export function GoalsTimeline({ goal }: GoalsTimelineProps) {
  const [selectedElement, setSelectedElement] = useState<string | null>(null);

  const bedtimeMinutes = timeToMinutes(goal.bedtime);
  const wakeUpMinutes = timeToMinutes(goal.wakeUpTime);

  // Use the goal's night sleep duration directly
  const nightSleepMinutes = goal.totalNightSleep * 60;

  // Calculate total nap time using goal values
  const totalDaySleepMinutes = goal.totalSleep24h * 60;
  const totalNapMinutes = totalDaySleepMinutes - nightSleepMinutes;
  const napDurationMinutes = goal.numNaps > 0 ? totalNapMinutes / goal.numNaps : 0;

  // Calculate awake time during the day
  const awakeMinutes = wakeUpMinutes > bedtimeMinutes
    ? bedtimeMinutes + (24 * 60 - wakeUpMinutes)
    : bedtimeMinutes - wakeUpMinutes;

  // Distribute naps evenly during awake time
  const napStartTimes: number[] = [];
  if (goal.numNaps > 0 && awakeMinutes > 0) {
    // Calculate the ideal awake window to space naps evenly
    const totalNapTime = napDurationMinutes * goal.numNaps;
    const totalAwakeTime = awakeMinutes - totalNapTime;
    const awakeWindowMinutes = totalAwakeTime / (goal.numNaps + 1);

    let currentTime = wakeUpMinutes;

    for (let i = 0; i < goal.numNaps; i++) {
      // Space naps evenly
      currentTime += awakeWindowMinutes;

      // Ensure nap doesn't go past bedtime (accounting for 24h wrap)
      const napStart = currentTime % (24 * 60);
      napStartTimes.push(napStart);

      currentTime += napDurationMinutes;
    }
  }

  // Create timeline segments (in 24-hour view from 6 AM to 6 AM next day)
  const startHour = 6; // Start timeline at 6 AM
  const timelineStartMinutes = startHour * 60;

  // Calculate positions (as percentages)
  const getPosition = (minutes: number) => {
    let adjustedMinutes = minutes;
    // If time is before startHour, add 24 hours
    if (minutes < timelineStartMinutes) {
      adjustedMinutes = minutes + (24 * 60);
    }
    return ((adjustedMinutes - timelineStartMinutes) / (24 * 60)) * 100;
  };

  const bedtimePosition = getPosition(bedtimeMinutes);
  const wakeUpPosition = getPosition(wakeUpMinutes);

  // Night sleep spans from bedtime to wake-up
  let nightSleepWidth: number;
  if (wakeUpPosition > bedtimePosition) {
    nightSleepWidth = wakeUpPosition - bedtimePosition;
  } else {
    // Spans midnight
    nightSleepWidth = (100 - bedtimePosition) + wakeUpPosition;
  }

  // Hour markers (every 3 hours from 6 AM)
  const hourMarkers = [6, 9, 12, 15, 18, 21, 0, 3];

  return (
    <div className="space-y-3">
      <div className="mb-1">
        <h3 className="text-sm font-medium">Daily Sleep Guide</h3>
      </div>

      {/* Timeline Container */}
      <div className="relative">
        {/* Background track */}
        <div className="h-8 bg-muted rounded-full relative">
          {/* Night sleep period */}
          <div
            className="absolute h-full bg-primary rounded-full cursor-pointer active:scale-95 transition-transform"
            style={{
              left: `${bedtimePosition}%`,
              width: `${nightSleepWidth}%`,
            }}
            onClick={() => setSelectedElement(selectedElement === 'night' ? null : 'night')}
            title={`Night Sleep: ${goal.bedtime} - ${goal.wakeUpTime} (${formatDuration(nightSleepMinutes)})`}
          >
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <Moon className="h-3 w-3 text-primary-foreground sm:h-4 sm:w-4" />
            </div>
          </div>

          {/* Nap periods */}
          {napStartTimes.map((napStart, index) => {
            const napPosition = getPosition(napStart);
            const napWidth = (napDurationMinutes / (24 * 60)) * 100;
            const napEnd = (napStart + napDurationMinutes) % (24 * 60);

            return (
              <div
                key={index}
                className="absolute h-full bg-accent rounded-full cursor-pointer active:scale-95 transition-transform"
                style={{
                  left: `${napPosition}%`,
                  width: `${napWidth}%`,
                }}
                onClick={() => setSelectedElement(selectedElement === `nap-${index}` ? null : `nap-${index}`)}
                title={`Nap ${index + 1}: ${minutesToTimeString(napStart)} - ${minutesToTimeString(napEnd)} (${formatDuration(napDurationMinutes)})`}
              >
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <Cloud className="h-2 w-2 text-accent-foreground sm:h-3 sm:w-3" />
                </div>
              </div>
            );
          })}
        </div>

        {/* Hour labels */}
        <div className="relative h-5 mt-1">
          {hourMarkers.map((hour, index) => {
            const minutes = hour * 60;
            const position = getPosition(minutes);
            const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
            const period = hour < 12 ? 'am' : 'pm';
            const showLabel = hour === 6 || hour === 12 || hour === 18 || hour === 0;

            return (
              <div
                key={index}
                className="absolute text-[10px] text-muted-foreground"
                style={{ left: `${position}%`, transform: 'translateX(-50%)' }}
              >
                {showLabel ? `${displayHour}${period}` : ''}
              </div>
            );
          })}
        </div>
      </div>

      {/* Tooltip Info */}
      {selectedElement && (
        <div className="mt-2 p-3 bg-muted/50 rounded-lg text-sm">
          {selectedElement === 'night' ? (
            <div>
              <div className="font-semibold flex items-center gap-2">
                <Moon className="h-4 w-4 text-primary" />
                Night Sleep
              </div>
              <div className="mt-1 text-muted-foreground">
                {goal.bedtime} - {goal.wakeUpTime}
              </div>
              <div className="text-xs text-muted-foreground">
                Duration: {formatDuration(nightSleepMinutes)}
              </div>
            </div>
          ) : selectedElement.startsWith('nap-') ? (
            <div>
              {napStartTimes.map((napStart, index) => {
                if (selectedElement === `nap-${index}`) {
                  const napEnd = (napStart + napDurationMinutes) % (24 * 60);
                  return (
                    <div key={index}>
                      <div className="font-semibold flex items-center gap-2">
                        <Cloud className="h-4 w-4 text-accent" />
                        Nap {index + 1}
                      </div>
                      <div className="mt-1 text-muted-foreground">
                        {minutesToTimeString(napStart)} - {minutesToTimeString(napEnd)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Duration: {formatDuration(napDurationMinutes)}
                      </div>
                    </div>
                  );
                }
                return null;
              })}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

